import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type TopMediaOutletsView = {
  /** `Most stories` — the top bar's outlet, its story count as the badge. */
  kpis: KPIMetric[];
  /** One row per outlet, ranked by stories; one key per sentiment. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any story is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type TopMediaOutletsCardProps = WidgetBlockProps & {
  view?: TopMediaOutletsView;
  /** Opens the stories behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:18143` — which publications write about us, and how.
 *
 * Counts, not shares, and ranked by them — so the outlet the KPI names is the
 * top bar and its length is the reason. A normalised plot would draw eight
 * equal bars and put the whole ranking in the sort order, which is the one
 * reading "Top …" cannot afford to lose. No limit: the corpus carries eight
 * outlets, and all eight fit.
 *
 * Earned media only — the rows arrive already cut to coverage.
 */
export function TopMediaOutletsCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: TopMediaOutletsCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No coverage in this period"
      {...cardState(status, hasData, onRetry)}
      title="Top media outlets by sentiment"
      helpText="Which publications are writing about us, and how each one is writing about us."
      helpSupportingText="Stories rather than share, so the outlet giving us the most coverage reads as the longest bar. Only mentions a media organisation actually published are counted — a reader's reply has no publisher and is not coverage."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
